import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { Toaster } from 'sonner';
import './App.css';

// Contexts
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LadderProvider, useLadder } from './contexts/LadderContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { BrandingProvider } from './contexts/BrandingContext';

// Components
import { PublicHeader, MobileBottomNav, PhidelBadge } from './components/PublicHeader';
import { AdminSidebar } from './components/AdminSidebar';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import { Button } from './components/ui/button';

// Pages
import Home from './pages/Home';
import Leaderboard from './pages/Leaderboard';
import Scores from './pages/Scores';
import Partners from './pages/Partners';
import Rules from './pages/Rules';
import PlayerProfile from './pages/PlayerProfile';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminPlayers from './pages/AdminPlayers';
import AdminMatches from './pages/AdminMatches';
import AdminSettings from './pages/AdminSettings';
import AdminTranslations from './pages/AdminTranslations';
import AdminRulesEditor from './pages/AdminRulesEditor';
import AdminPhidelLevelEditor from './pages/AdminPhidelLevelEditor';
import AdminExport from './pages/AdminExport';
import AdminManagement from './pages/AdminManagement';

// Public Layout with Bottom Nav for Mobile
const PublicLayout = () => {
  return (
    <div className="public-layout" data-testid="public-layout">
      <PublicHeader />
      <main className="public-main">
        <Outlet />
      </main>
      {/* Footer - appears at end of content, not sticky, same on desktop and mobile */}
      <footer className="public-footer" data-testid="public-footer">
        <PhidelBadge />
      </footer>
      <MobileBottomNav />
    </div>
  );
};

// Admin Layout with Auth Protection
const AdminLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-muted border-t-primary rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="admin-layout" data-testid="admin-layout">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-20 flex items-center px-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-sidebar-toggle"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <span className="ml-3 font-heading font-semibold text-foreground">Admin</span>
      </div>
      
      <main className="admin-content pt-16 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
};

// Initialize ladder if none exists
const LadderInitializer = ({ children }) => {
  const { ladder, createLadder, loading } = useLadder();
  const { isAuthenticated } = useAuth();
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    const initLadder = async () => {
      if (!loading && !ladder && isAuthenticated && !initializing) {
        setInitializing(true);
        try {
          await createLadder('Padel Ladder');
        } catch (e) {
          console.error('Failed to create ladder:', e);
        }
        setInitializing(false);
      }
    };
    initLadder();
  }, [loading, ladder, isAuthenticated, createLadder, initializing]);

  return children;
};

// Toaster with theme support
const ThemedToaster = () => {
  const { isDark } = useTheme();
  
  return (
    <Toaster 
      position="top-right" 
      theme={isDark ? 'dark' : 'light'}
      toastOptions={{
        style: {
          background: isDark ? 'hsl(222 25% 13%)' : '#fff',
          border: isDark ? '1px solid hsl(222 20% 22%)' : '1px solid #e2e8f0',
          borderRadius: '0.75rem',
          color: isDark ? 'hsl(210 40% 96%)' : 'inherit'
        }
      }}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <LadderProvider>
            <BrandingProvider>
              <LadderInitializer>
                <BrowserRouter>
                  <Routes>
                    {/* Public Routes */}
                    <Route element={<PublicLayout />}>
                      <Route path="/" element={<Home />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/scores" element={<Scores />} />
                      <Route path="/partners" element={<Partners />} />
                      <Route path="/rules" element={<Rules />} />
                      <Route path="/phidel-level" element={<Navigate to="/rules?tab=level" replace />} />
                      <Route path="/p/:id/:slug" element={<PlayerProfile />} />
                    </Route>

                    {/* Admin Login */}
                  <Route path="/admin/login" element={<AdminLogin />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="players" element={<AdminPlayers />} />
                    <Route path="matches" element={<AdminMatches />} />
                    {/* Super Admin Only Routes */}
                    <Route path="settings" element={<SuperAdminRoute><AdminSettings /></SuperAdminRoute>} />
                    <Route path="translations" element={<SuperAdminRoute><AdminTranslations /></SuperAdminRoute>} />
                    <Route path="rules-editor" element={<SuperAdminRoute><AdminRulesEditor /></SuperAdminRoute>} />
                    <Route path="phidel-level-editor" element={<SuperAdminRoute><AdminPhidelLevelEditor /></SuperAdminRoute>} />
                    <Route path="export" element={<SuperAdminRoute><AdminExport /></SuperAdminRoute>} />
                    <Route path="admins" element={<SuperAdminRoute><AdminManagement /></SuperAdminRoute>} />
                  </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </BrowserRouter>
                <ThemedToaster />
              </LadderInitializer>
            </BrandingProvider>
          </LadderProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
